import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { ReactSVG } from 'react-svg';
import { Theme } from '@mui/material';
import { styled } from '@mui/material';
import { IconBolt, IconInputSpark } from '@tabler/icons-react';

import intentRoutingIcon from '../../assets/icons/intent-routing.svg';
import { useAutopilotConfig } from '../../email-builder-page/intent-email-builder/components/autopilot/useAutopilotConfig';
import {
  useEmailMode,
  useGetAutoflowWorkflowOrAutoflowTemplate,
  useGetBuilderQueryParams,
  useGetIsTemplateIntentWorkflow,
} from '../../hooks';
import { sortTools } from '../../workflow-builder/business-logic/helpers';
import { getWorkflowValueFromSearch } from '../helpers';
import { useGetUsableTools } from '../hooks';
import { ACCEPTABLE_CV_TYPES } from './constants';
import { CircleIcon } from './StyledComponents';
import groupBy from 'lodash/fp/groupBy';
import { useOrchestratorAutoflowContext } from 'src/components/autoflow-v2-editor/useOrchestratorAutoflowContext';
import {
  useGetIntentsQueryWithProduct,
  useIsFeatureFlagEnabled,
} from 'src/hooks/hooks';
import { useAutoflowVersionManager } from 'src/hooks/useAutoflowVersionManager';
import { useGetContextVariables } from 'src/hooks/useGetContextVariables';
import recommendedActionIcon from 'src/pages/workflow-builder-edit/assets/icons/recommended-action.svg';
import workflowIcon from 'src/pages/workflow-builder-edit/assets/icons/workflow.svg';
import zendeskActionIcon from 'src/pages/workflow-builder-edit/assets/icons/zendesk-action.svg';
import {
  useGetActionBuilderActionsQuery,
  useGetZendeskActionsQuery,
} from 'src/services/action-builder/actionBuilderApi';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import {
  useGetAvailableToolsQuery as useGetAvailableAutopilotToolsQuery,
  useGetOrCreateAutopilotWorkflowQuery,
} from 'src/services/email-autopilot/emailAutopilotApi';
import { AutopilotWorkflowModel } from 'src/services/email-autopilot/types';
import { isToolSelected } from 'src/services/email-autopilot/utils';
import { useGetOrCreateOrchestratorAutoflowWorkflowQuery } from 'src/services/orchestrator-autoflow/orchestratorAutoflowApi';
import { useGetAutoflowToolsQuery } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import {
  Action,
  ContextVariable,
  isMcpActionFields,
} from 'src/types/actionBuilderApiTypes';
import {
  AutoflowTool,
  AutoflowToolActionFields,
  ToolDefinitionResponse,
  UsableTool,
} from 'src/types/workflowBuilderAPITypes';
import {
  generateSelectedMcpActionURL,
  generateSelectedZendeskActionURL,
  getVariablesFromAction,
  replaceActionIdInRoute,
} from 'src/utils/actionBuilder/helpers';
import { CHANNEL_TO_PRODUCT_MAP } from 'src/utils/constants';
import { Routes } from 'src/utils/enums';
import { isVoiceLikeChannel } from 'src/utils/solve/channelUtils';
import { getEffectiveWorkflowMode } from 'src/utils/solve/intentUtils';

export const getIconForTool = (
  tool: AutoflowTool,
  theme: Theme,
  isRecommended: boolean,
) => {
  switch (tool.tool_type) {
    case 'demo':
    case 'mcp':
    case 'api_call':
    case 'template_api_call':
    case 'system_built_in':
    case 'open_side_conversation':
    case 'browser_agent':
    case 'wait':
      if (isRecommended) {
        return <img alt='' src={recommendedActionIcon} />;
      }
      return (
        <CircleIcon bgcolor={theme.palette.colors.blue[500]}>
          <IconBolt size={20} />
        </CircleIcon>
      );
    case 'hand_off':
    case 'intent_routing':
      return (
        <CircleIcon bgcolor='#1F866A'>
          <Icon src={intentRoutingIcon} />
        </CircleIcon>
      );
    case 'custom_event':
      return (
        <CircleIcon bgcolor='#079DB7'>
          <IconInputSpark size={20} />
        </CircleIcon>
      );
    case 'workflow':
      return (
        <CircleIcon bgcolor='#B920C7'>
          <Icon src={workflowIcon} />
        </CircleIcon>
      );
    case 'zendesk_flowstate_action':
      return <img alt='' height={32} src={zendeskActionIcon} width={32} />;
    default:
      return null;
  }
};

/**
 * Converts MCP tool identifiers into human-readable names.
 *
 * Examples:
 * - "mcp___add_tools"       -> "Add Tools"
 * - "mcp___add-tools-text"  -> "Add Tools Text"
 *
 * If the input does not match the MCP naming pattern, the original string is returned.
 */
export const getMCPToolName = (toolName: string) => {
  // detects text like mcp___tool_name or mcp___tool-name
  const regex = /^mcp___[a-z0-9]+([_-][a-z0-9]+)*$/i;

  if (!regex.test(toolName)) {
    return toolName;
  }

  // remove prefix
  const withoutPrefix = toolName.replace(/^mcp___/, '');

  // split by _ or -
  const words = withoutPrefix.split(/[-_]/);

  // capitalize first letter of each word
  const formatted = words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return formatted;
};

export const getNameForTool = (toolName: string) => {
  switch (toolName) {
    case 'get_response_with_web_search':
      return 'Get Response With Web Search';
    case 'search_help_center_articles':
      return 'Search Help Center Articles';
    case 'create_zendesk_ticket':
      return 'Create Zendesk Ticket';
    case 'create_gorgias_ticket':
      return 'Create Gorgias Ticket';
    case 'create_pylon_ticket':
      return 'Create Pylon Ticket';
    case 'create_intercom_ticket':
      return 'Create Intercom Ticket';
    case 'forward_phone_call':
      return 'Forward Phone Call';
    case 'create_ticket_in_helpdesk':
      return 'Create Ticket in Helpdesk';
    case 'send_sms':
      return 'Send SMS';
    case 'create_freshdesk_ticket':
      return 'Create Freshdesk Ticket';
    case 'create_salesforce_case':
      return 'Create Salesforce Case';
    case 'append_transcript_to_salesforce_case':
      return 'Append Transcript to Salesforce Case';
    case 'open_side_conversation':
      return 'Human-in-the-loop';
    case 'create_hubspot_ticket':
      return 'Create Hubspot Ticket';
    case 'create_hubspot_helpdesk_ticket':
      return 'Create Hubspot Helpdesk Ticket';
    case 'end_phone_call':
      return 'End Phone Call';
    case 'send_verification_code':
      return 'Send Verification Code';
    case 'verify_verification_code':
      return 'Verify Verification Code';
    case 'create_dynamics_365_case':
      return 'Create Dynamics 365 Case';
    case 'create_servicenow_case':
      return 'Create ServiceNow Case';
    case 'create_servicenow_incident':
      return 'Create ServiceNow Incident';
    case 'sip_handoff':
      return 'SIP Handoff';
    case 'zendesk_sip_handoff':
      return 'Zendesk SIP Handoff';
    case 'nice_cxone_signal':
      return 'Nice CXone Signal';
    case 'twilio_pay':
      return 'Twilio Pay';
    case 'sms_collect_info':
      return 'SMS Collect Info';
    case 'wait':
      return 'Wait';
    default:
      return toolName;
  }
};

export const useGetHrefForTool = (tool: AutoflowTool) => {
  const { view } = useGetBuilderQueryParams();
  const { data } = useGetIntentsQueryWithProduct();
  const isClassicWorkflowsDeprecated = useIsFeatureFlagEnabled(
    'classic_workflows_deprecated',
  );
  const { tool_id: toolId, tool_type: toolType } = tool;

  const isToolIdIntentId =
    toolType === 'hand_off' ||
    toolType === 'intent_routing' ||
    toolType === 'workflow';

  const intent = useMemo(() => {
    if (!isToolIdIntentId) {
      return;
    }

    return data?.intents.find(intent => intent.intent_definition_id === toolId);
  }, [data?.intents, isToolIdIntentId, toolId]);

  if (toolType === 'mcp') {
    return generateSelectedMcpActionURL(toolId);
  }

  if (toolType === 'zendesk_flowstate_action') {
    return generateSelectedZendeskActionURL(toolId);
  }

  if (
    toolType === 'api_call' ||
    toolType === 'template_api_call' ||
    toolType === 'browser_agent'
  ) {
    return replaceActionIdInRoute(toolId);
  }

  if (isToolIdIntentId && intent) {
    const params = new URLSearchParams({
      intentId: toolId,
      mode: getEffectiveWorkflowMode(
        toolId,
        intent.is_autoflow,
        isClassicWorkflowsDeprecated,
      ),
      view,
      workflowId: intent.intent_workflow_id,
    });

    if (view === 'email') {
      params.set('emailMode', 'Interactive');
    }

    if (isVoiceLikeChannel(view)) {
      params.set('mode', 'autoflow');
    }

    return `${Routes.WORKFLOW_BUILDER_EDIT}?${params}`;
  }

  return null;
};

export const getActionFieldsForSystemBuiltInTool = (
  toolId: string,
): AutoflowToolActionFields => {
  const actionFields: AutoflowToolActionFields = {
    description: '',
    dynamicListOutputVariables: [],
    inputVariables: [],
    isDisabled: false,
    outputVariables: [],
    systemBuiltInToolExamplePolicy: '',
    systemBuiltInToolInput: [],
  };

  switch (toolId) {
    case 'create_intercom_ticket':
      actionFields.description =
        'Creates a support ticket in the integrated Intercom help center';
      actionFields.systemBuiltInToolInput = [
        'ticket_title (string): The title of the support ticket',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_intercom_ticket tool to generate a new ticket in Intercom. The ticket_title should be in the format: [The format you need]';
      return actionFields;
    case 'create_zendesk_ticket':
      actionFields.description =
        'Creates a support ticket in the integrated Zendesk help center';
      actionFields.systemBuiltInToolInput = [
        'ticket_title (string): The title of the support ticket',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_zendesk_ticket tool to generate a new ticket in Zendesk. The ticket_title should be in the format: [The format you need]';
      return actionFields;
    case 'create_gorgias_ticket':
      actionFields.description =
        'Creates a support ticket in the integrated Gorgias help center';
      actionFields.systemBuiltInToolInput = [
        'ticket_title (string): The title of the support ticket',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_gorgias_ticket tool to generate a new ticket in Gorgias. The ticket_title should be in the format: [The format you need]';
      return actionFields;
    case 'create_pylon_ticket':
      actionFields.description =
        'Creates a support ticket in the integrated Pylon help center';
      actionFields.systemBuiltInToolInput = [
        'ticket_title (string): The title of the support ticket',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_pylon_ticket tool to generate a new ticket in Pylon. The ticket_title should be in the format: [The format you need]';
      return actionFields;
    case 'create_freshdesk_ticket':
      actionFields.description =
        'Creates a support ticket in the integrated Freshdesk help center';
      actionFields.systemBuiltInToolInput = [
        'ticket_title (string): The title of the support ticket',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_freshdesk_ticket tool to generate a new ticket in Freshdesk. The ticket_title should be in the format: [The format you need]';
      return actionFields;
    case 'create_salesforce_case':
      actionFields.description =
        'Creates a support case in the integrated Salesforce help center';
      actionFields.systemBuiltInToolInput = [
        'case_title (string): The title of the support case',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_salesforce_case tool to generate a new case in Salesforce. The case_title should be in the format: [The format you need]';
      return actionFields;
    case 'append_transcript_to_salesforce_case':
      actionFields.description =
        'Appends the current Voice conversation transcript as plain-text comments to an existing Salesforce case';
      actionFields.systemBuiltInToolInput = [
        'case_id (string): The Salesforce ID of the existing case',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'For an existing Salesforce case, use the append_transcript_to_salesforce_case tool with case_id [Salesforce case ID]. If create_salesforce_case was called first, pass its returned ticket_id as case_id and note that the transcript will appear in both the case Description and case comments';
      return actionFields;
    case 'create_hubspot_ticket':
      actionFields.description =
        'Creates a support ticket in the integrated Hubspot help center';
      actionFields.systemBuiltInToolInput = [
        'ticket_title (string): The title of the support ticket',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_hubspot_ticket tool to generate a new ticket in Hubspot. The ticket_title should be in the format: [The format you need]';
      return actionFields;
    case 'create_hubspot_helpdesk_ticket':
      actionFields.description =
        'Creates a support ticket in the integrated Hubspot helpdesk';
      actionFields.systemBuiltInToolInput = [
        'ticket_title (string): The title of the support ticket',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_hubspot_helpdesk_ticket tool to generate a new ticket in Hubspot. The ticket_title should be in the format: [The format you need]';
      return actionFields;
    case 'search_help_center_articles':
      actionFields.description =
        'Searches through articles for a proper answer to a given query';
      actionFields.systemBuiltInToolInput = [
        'query (string): the query for the search engine to retrieve relevant articles',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        "Use the search_help_center_articles tool to locate articles that are relevant to the customer's question";
      return actionFields;
    case 'get_response_with_web_search':
      actionFields.description =
        'Use model with access to web search to generate response';
      actionFields.systemBuiltInToolInput = [
        'prompt (string): the prompt for the model with web search enabled',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the get_response_with_web_search tool generate a response using web search';
      return actionFields;
    case 'forward_phone_call':
      actionFields.description =
        'Forwards phone call to a specified phone number';
      actionFields.systemBuiltInToolExamplePolicy =
        'Forwards the user to phone number [Phone number of E.164 format, e.g. +14151234567]';
      return actionFields;
    case 'send_sms':
      actionFields.description =
        'Send SMS message of customized content to a specified phone number.';
      actionFields.systemBuiltInToolInput = [
        'phone_number (string): the phone number to send message to (E.164 format, e.g. +14151234567)',
        'message (string): the message that will be sent',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the the send_sms tool to send the message [Your message] to phone number [Phone number]';
      return actionFields;
    case 'sms_collect_info':
      actionFields.description =
        'Collect text and image responses from users over SMS.';
      actionFields.systemBuiltInToolExamplePolicy =
        'Trigger SMS_collect_info to ask user to send a photo of their receipt';
      return actionFields;
    case 'end_phone_call':
      actionFields.description = 'Ends the current phone call.';
      actionFields.systemBuiltInToolInput = [
        'message (string): the message to tell the user before ending the call',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Say [Your message] to the user, then use the end_phone_call tool to end the call';
      return actionFields;
    case 'send_verification_code':
      actionFields.description =
        'Sends a verification code to a specified phone number via SMS';
      actionFields.systemBuiltInToolInput = [
        'phone_number (string): the phone number to send the verification code to (E.164 format, e.g. +14151234567)',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the send_verification_code tool to send a verification code to phone number [Phone number]';
      return actionFields;
    case 'verify_verification_code':
      actionFields.description =
        'Verifies a verification code sent to a specified phone number';
      actionFields.systemBuiltInToolInput = [
        'phone_number (string): the phone number to verify the verification code for (E.164 format, e.g. +14151234567)',
        'code (string): the verification code to verify',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the verify_verification_code tool to verify the code [User provided code] for phone number [Phone number]';
      return actionFields;
    case 'get_response_with_web_search':
      actionFields.description =
        'Searches the internet to generate an answer for user query';
      actionFields.systemBuiltInToolInput = [
        'query (string): the user query to search for',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the get_response_with_web_search tool to search and generate a response for the query [User query]';
      return actionFields;
    case 'create_dynamics_365_case':
      actionFields.description =
        'Creates a support case in the integrated Dynamics 365 CRM';
      actionFields.systemBuiltInToolInput = [
        'case_title (string): The title of the support case',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_dynamics_365_case tool to generate a new case in Dynamics 365. The case_title should be in the format: [The format you need]';
      return actionFields;
    case 'create_servicenow_case':
      actionFields.description =
        'Creates a case in the integrated ServiceNow instance';
      actionFields.systemBuiltInToolInput = [
        'case_title (string): The title of the case, used as the short description',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_servicenow_case tool to generate a new case in ServiceNow. The case_title should be in the format: [The format you need]';
      return actionFields;
    case 'create_servicenow_incident':
      actionFields.description =
        'Creates an incident in the integrated ServiceNow instance';
      actionFields.systemBuiltInToolInput = [
        'case_title (string): The title of the incident, used as the short description',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the create_servicenow_incident tool to generate a new incident in ServiceNow. The case_title should be in the format: [The format you need]';
      return actionFields;
    case 'sip_handoff':
      actionFields.description =
        'Transfers the phone call to a specified SIP domain';
      actionFields.systemBuiltInToolInput = [
        'sip_uri (string): The SIP URI to transfer the call to (optional, overrides configured domain)',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the sip_handoff tool to transfer the call to [SIP URI or configured default domain]';
      return actionFields;
    case 'zendesk_sip_handoff':
      actionFields.description =
        'Creates a Zendesk ticket with conversation transcript and transfers the call via SIP to the configured endpoint. The ticket ID is included in the SIP header for routing.';
      actionFields.systemBuiltInToolInput = [
        'ticket_title (string): The title/subject for the Zendesk ticket',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        "Use the zendesk_sip_handoff tool to create a Zendesk ticket and transfer the call to a SIP endpoint. The ticket_title should summarize the customer's issue.";
      return actionFields;
    case 'nice_cxone_signal':
      actionFields.description =
        'Signals Nice CXone to transfer the current call back to a human agent';
      actionFields.systemBuiltInToolExamplePolicy =
        'Use the nice_cxone_signal tool to transfer the call back to human agent in Nice CXone';
      return actionFields;
    case 'twilio_pay':
      actionFields.description =
        'Securely collects card details from the caller via Twilio <Pay>. The card is tokenized by a PCI-compliant third party (never reaches Forethought infrastructure) and a payment token is written to context variables for a downstream charge step.';
      actionFields.systemBuiltInToolInput = [
        'customer_facing_message (string, optional): Message to speak before starting the payment flow',
      ];
      actionFields.systemBuiltInToolExamplePolicy =
        'After confirming the debt amount with the caller, use the twilio_pay tool to collect their card details. Then proceed to the charge step.';
      return actionFields;
    default:
      return actionFields;
  }
};

const getActionFieldsForMCPTool = (
  actions: Action[] | undefined,
  tool: AutoflowTool,
): AutoflowToolActionFields => {
  const actionFields: AutoflowToolActionFields = {
    connectorName: '',
    description: '',
    dynamicListOutputVariables: [],
    inputVariables: [],
    isDisabled: false,
    outputVariables: [],
  };

  const action = actions?.find(action => action.action_id === tool.tool_id);

  if (!action || !isMcpActionFields(action.action_fields)) {
    return actionFields;
  }

  const { action_description, action_fields, is_deleted } = action;

  const description = action_description || '';
  const connectorName = action_fields.connector_name || 'n/a';

  return { ...actionFields, connectorName, description, isDeleted: is_deleted };
};

export const useGetActionFieldsForTool = (
  tool: AutoflowTool,
): AutoflowToolActionFields => {
  const defaults = {
    description: '',
    dynamicListOutputVariables: [],
    inputVariables: [],
    isDisabled: false,
    outputVariables: [],
  };

  const { data } = useGetActionBuilderActionsQuery({});
  const { contextVariables } = useGetContextVariables({
    shouldIncludeSystemContextVariables: true,
    shouldIncludeTemplateContextVariables: true,
  });
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isAutoflowActionDynamicListOutputEnabled = featureFlags.includes(
    'autoflow_action_dynamic_list_output',
  );
  const { data: zendeskData } = useGetZendeskActionsQuery(undefined);

  if (
    tool.tool_type === 'api_call' ||
    tool.tool_type === 'template_api_call' ||
    tool.tool_type === 'browser_agent'
  ) {
    const action = data?.actions.find(
      action => action.action_id === tool.tool_id,
    );
    if (!action) {
      return defaults;
    }
    const variables = getVariablesFromAction(action, contextVariables);

    const hasAcceptableInputVariables = variables.inputVariables.every(
      inputVariable =>
        ACCEPTABLE_CV_TYPES.includes(inputVariable.context_variable_type),
    );

    const hasDynamicListOutput = Boolean(
      variables.dynamicListOutputVariables?.length,
    );

    return {
      description: action?.action_description,
      ...variables,
      isDisabled:
        !hasAcceptableInputVariables ||
        (!isAutoflowActionDynamicListOutputEnabled && hasDynamicListOutput),
    };
  }

  if (tool.tool_type === 'system_built_in') {
    return getActionFieldsForSystemBuiltInTool(tool.tool_id);
  }

  if (tool.tool_type === 'mcp') {
    return getActionFieldsForMCPTool(data?.actions, tool);
  }

  if (tool.tool_type === 'zendesk_flowstate_action') {
    const action = zendeskData?.actions.find(
      action => action.action_id === tool.tool_id,
    );
    return {
      ...defaults,
      description: action?.action_description || '',
    };
  }

  if (tool.tool_type === 'open_side_conversation') {
    return {
      ...defaults,
      description: 'Triggers a side conversation with a human.',
      systemBuiltInToolExamplePolicy:
        'Use the Human-in-the-loop tool to trigger a conversation with a human.',
    };
  }

  if (tool.tool_type === 'wait') {
    return {
      ...defaults,
      description:
        'Pauses the agent for a configured duration before continuing. Useful when waiting for external systems to process data.',
      systemBuiltInToolExamplePolicy:
        'After triggering [external action], use the Wait tool, then proceed to check the result.',
    };
  }

  return defaults;
};

const Icon = styled(ReactSVG)`
  svg {
    [fill] {
      fill: ${props => props.theme.palette.colors.white};
    }
    [stroke] {
      stroke: ${props => props.theme.palette.colors.white};
    }
  }
`;

export const useGetAutoflowCvs = (
  intentWorkflowId: string | undefined,
): (ContextVariable & { isActive: boolean })[] => {
  const { autoflowWorkflow } =
    useGetAutoflowWorkflowOrAutoflowTemplate(intentWorkflowId);
  const isTemplate = useGetIsTemplateIntentWorkflow();

  // Get version context for version-aware active state
  const { versionData } = useAutoflowVersionManager();

  // Determine if we're in email autopilot mode
  const { view } = useGetBuilderQueryParams();
  const { emailMode } = useEmailMode();
  const isEmailAutopilot = view === 'email' && emailMode !== 'Interactive';

  // Get email autopilot data if in email autopilot mode
  const { configurationId, version: configurationVersion } =
    useAutopilotConfig();

  const { data: emailAutopilotData } = useGetOrCreateAutopilotWorkflowQuery(
    {
      configurationId: configurationId || '',
      version: configurationVersion ?? -1,
    },
    { skip: !isEmailAutopilot || !configurationId },
  );

  // Use version's context_variables if viewing a version, otherwise use current workflow's
  const { context_variables: contextVariablesInUse } =
    versionData ?? autoflowWorkflow ?? {};
  const emailContextVariablesInUse = emailAutopilotData?.context_variables;

  const { contextVariables } = useGetContextVariables({
    shouldIncludeSystemContextVariables: true,
    shouldIncludeTemplateContextVariables: isTemplate,
  });

  const cvs = useMemo(() => {
    return (
      contextVariables
        .map(workflowCv => ({
          ...workflowCv,
          isActive:
            isEmailAutopilot && emailContextVariablesInUse
              ? Boolean(
                  emailContextVariablesInUse.find(
                    cvInUse =>
                      cvInUse.context_variable_id ===
                      workflowCv.context_variable_id,
                  ),
                )
              : Boolean(
                  contextVariablesInUse?.find(
                    cvInUse => cvInUse === workflowCv.context_variable_id,
                  ),
                ),
        }))
        // Private CVs are not exposed to the AI model, so they can't be added to
        // an autoflow policy (the backend rejects the PATCH). Already-attached
        // ones stay listed so they remain visible and removable. Email autopilot
        // strips private CVs from its prompt server-side, so it keeps them.
        .filter(
          cv =>
            !cv.is_private_context_variable || cv.isActive || isEmailAutopilot,
        )
    );
  }, [
    contextVariables,
    contextVariablesInUse,
    isEmailAutopilot,
    emailContextVariablesInUse,
  ]);

  return cvs;
};

export const useGetIsToolActive = (
  toolId: string,
  toolType: string,
): boolean => {
  const { pathname, search } = useLocation();
  const intentWorkflowId = getWorkflowValueFromSearch(search, 'workflowId');

  const isOrchestratorContext = pathname.includes('/orchestrator');
  const orchestratorContext = useOrchestratorAutoflowContext();
  const orchestratorWorkflowId = orchestratorContext.workflowId;
  const orchestratorStepId = orchestratorContext.stepId;

  // Get version context for version-aware active state
  const { versionData } = useAutoflowVersionManager();

  const { autoflowWorkflow, isLoading } =
    useGetAutoflowWorkflowOrAutoflowTemplate(intentWorkflowId);

  const {
    data: orchestratorAutoflowWorkflow,
    isLoading: isLoadingOrchestrator,
  } = useGetOrCreateOrchestratorAutoflowWorkflowQuery(
    {
      stepId: orchestratorStepId || '',
      workflowId: orchestratorWorkflowId || '',
    },
    {
      skip:
        !isOrchestratorContext ||
        !orchestratorWorkflowId ||
        !orchestratorStepId,
    },
  );

  // Determine if we're in email autopilot mode
  const { view } = useGetBuilderQueryParams();
  const { emailMode } = useEmailMode();
  const isEmailAutopilot = view === 'email' && emailMode !== 'Interactive';

  // Get email autopilot data if in email autopilot mode
  const { configurationId, version: configurationVersion } =
    useAutopilotConfig();

  const { data: emailAutopilotData } = useGetOrCreateAutopilotWorkflowQuery(
    {
      configurationId: configurationId || '',
      version: configurationVersion ?? -1,
    },
    { skip: !isEmailAutopilot || !configurationId },
  );

  if (isLoading || isLoadingOrchestrator) {
    return true;
  }

  if (isOrchestratorContext && orchestratorAutoflowWorkflow) {
    const { tool_definitions: toolDefinitions } = orchestratorAutoflowWorkflow;
    const toolDefinition = toolDefinitions?.find(
      toolDefinition =>
        toolDefinition.tool_id === toolId &&
        toolDefinition.tool_type === toolType,
    );
    return Boolean(toolDefinition);
  }

  if (isEmailAutopilot && emailAutopilotData) {
    return isToolSelected(emailAutopilotData, toolId);
  }

  // Use version's tool_definitions if viewing a version, otherwise use current workflow's
  const { tool_definitions: toolDefinitions } =
    versionData ?? autoflowWorkflow ?? {};

  const toolDefinition = toolDefinitions?.find(
    toolDefinition =>
      toolDefinition.tool_id === toolId &&
      toolDefinition.tool_type === toolType,
  );

  return Boolean(toolDefinition);
};

export const useGetIsActionToolDeleted = (toolId: string) => {
  const { data } = useGetActionBuilderActionsQuery({});

  const action = data?.actions.find(action => action.action_id === toolId);

  return Boolean(action?.is_deleted);
};

export const useGetAutoflowToolDefinition = (
  toolId: string,
  toolType: string,
): ToolDefinitionResponse | undefined => {
  const { search } = useLocation();
  const intentWorkflowId = getWorkflowValueFromSearch(search, 'workflowId');

  // Get version context for version-aware tool definition
  const { versionData } = useAutoflowVersionManager();

  const { autoflowWorkflow, isLoading } =
    useGetAutoflowWorkflowOrAutoflowTemplate(intentWorkflowId);

  if (isLoading) {
    return undefined;
  }

  // Use version's tool_definitions if viewing a version, otherwise use current workflow's
  const { tool_definitions: toolDefinitions } =
    versionData ?? autoflowWorkflow ?? {};

  const toolDefinition = toolDefinitions?.find(
    toolDefinition =>
      toolDefinition.tool_id === toolId &&
      toolDefinition.tool_type === toolType,
  );

  return toolDefinition;
};

export const useGetToolName = (toolId: string, toolType: string): string => {
  const { view } = useGetBuilderQueryParams();
  const { emailMode } = useEmailMode();
  const isEmailAutopilot = view === 'email' && emailMode !== 'Interactive';

  // Get email autopilot data if in email autopilot mode
  const { data: availableAutopilotTools = [] } =
    useGetAvailableAutopilotToolsQuery(undefined, { skip: !isEmailAutopilot });

  // Get regular tools for widget channel
  const { data: toolsData } = useGetAutoflowToolsQuery({
    product: CHANNEL_TO_PRODUCT_MAP[view],
  });
  const { tools: widgetChannelTools = [] } = toolsData ?? {};

  const availableTools = isEmailAutopilot
    ? availableAutopilotTools
    : widgetChannelTools;

  const tool = availableTools.find(
    tool => tool.tool_id === toolId && tool.tool_type === toolType,
  );

  // A go-to/workflow route whose target was deactivated drops out of the
  // available set; fall back to the name captured in the policy definition so
  // the placed node still renders with its label.
  const toolDefinition = useGetAutoflowToolDefinition(toolId, toolType);

  return tool?.tool_name || toolDefinition?.name || '';
};

export const useGetIsCvActive = (cvId: string): boolean => {
  const { pathname, search } = useLocation();
  const intentWorkflowId = getWorkflowValueFromSearch(search, 'workflowId');

  const isOrchestratorContext = pathname.includes('/orchestrator');
  const orchestratorContext = useOrchestratorAutoflowContext();
  const orchestratorWorkflowId = orchestratorContext.workflowId;
  const orchestratorStepId = orchestratorContext.stepId;

  // Get version context for version-aware active state
  const { versionData } = useAutoflowVersionManager();

  const { autoflowWorkflow, isLoading } =
    useGetAutoflowWorkflowOrAutoflowTemplate(intentWorkflowId);

  const {
    data: orchestratorAutoflowWorkflow,
    isLoading: isLoadingOrchestrator,
  } = useGetOrCreateOrchestratorAutoflowWorkflowQuery(
    {
      stepId: orchestratorStepId || '',
      workflowId: orchestratorWorkflowId || '',
    },
    {
      skip:
        !isOrchestratorContext ||
        !orchestratorWorkflowId ||
        !orchestratorStepId,
    },
  );

  // Determine if we're in email autopilot mode
  const { view } = useGetBuilderQueryParams();
  const { emailMode } = useEmailMode();
  const isEmailAutopilot = view === 'email' && emailMode !== 'Interactive';

  // Get email autopilot data if in email autopilot mode
  const { configurationId, version: configurationVersion } =
    useAutopilotConfig();

  const { data: emailAutopilotData } = useGetOrCreateAutopilotWorkflowQuery(
    {
      configurationId: configurationId || '',
      version: configurationVersion ?? -1,
    },
    { skip: !isEmailAutopilot || !configurationId },
  );

  if (isLoading || isLoadingOrchestrator) {
    return true;
  }

  if (isOrchestratorContext && orchestratorAutoflowWorkflow) {
    const { context_variables: contextVariablesInUse } =
      orchestratorAutoflowWorkflow;
    const isActive = Boolean(
      contextVariablesInUse?.find(
        cvInUse => cvInUse.context_variable_id === cvId,
      ),
    );
    return isActive;
  }

  if (isEmailAutopilot && emailAutopilotData) {
    return Boolean(
      emailAutopilotData.context_variables?.find(
        cvInUse => cvInUse.context_variable_id === cvId,
      ),
    );
  }

  // Use version's context_variables if viewing a version, otherwise use current workflow's
  const { context_variables: contextVariablesInUse } =
    versionData ?? autoflowWorkflow ?? {};

  return Boolean(contextVariablesInUse?.find(cvInUse => cvInUse === cvId));
};

export const useGetEmailAutopilotFormattedTools = (
  isEmailAutopilot: boolean,
): {
  customEventTools?: UsableTool[];
  nonHandoffTools: UsableTool[];
  rerouteTools: UsableTool[];
  workflowTools: UsableTool[];
} => {
  // Get config from context (set by AutopilotPolicyView)
  const { configurationId, version: configurationVersion } =
    useAutopilotConfig();

  const { data: emailAutopilotTools = [] } = useGetAvailableAutopilotToolsQuery(
    undefined,
    {
      skip: !isEmailAutopilot,
    },
  );

  const { data: autopilotWorkflow } = useGetOrCreateAutopilotWorkflowQuery(
    {
      configurationId: configurationId || '',
      version: configurationVersion ?? -1,
    },
    { skip: !isEmailAutopilot || !configurationId },
  );

  return useMemo(() => {
    if (
      !isEmailAutopilot ||
      !autopilotWorkflow ||
      !emailAutopilotTools.length
    ) {
      return {
        customEventTools: [],
        nonHandoffTools: [],
        rerouteTools: [],
        workflowTools: [],
      };
    }

    // Map email autopilot tools to have correct isActive property
    const toolsWithCorrectActiveState = emailAutopilotTools.map(tool => ({
      ...tool,
      isActive: isToolSelected(autopilotWorkflow, tool.tool_id),
      // Add missing properties that might be expected
      toolDefinition: autopilotWorkflow.tool_definitions.find(
        td => td.tool_id === tool.tool_id,
      ),
    }));

    // Group tools by type (similar to regular formatted tools)
    const nonHandoffTools = toolsWithCorrectActiveState.filter(tool =>
      [
        'api_call',
        'template_api_call',
        'demo',
        'system_built_in',
        'open_side_conversation',
        'mcp',
        'browser_agent',
        'wait',
      ].includes(tool.tool_type),
    );
    const customEventTools = toolsWithCorrectActiveState.filter(
      tool => tool.tool_type === 'custom_event',
    );
    const rerouteTools = toolsWithCorrectActiveState.filter(
      tool =>
        (tool.tool_type === 'hand_off' ||
          tool.tool_type === 'intent_routing') &&
        tool.tool_id !== autopilotWorkflow.intent_definition_id,
    );

    return {
      customEventTools,
      nonHandoffTools,
      rerouteTools,
      workflowTools: [],
    };
  }, [isEmailAutopilot, autopilotWorkflow, emailAutopilotTools]);
};

export const useGetFormattedTools = (): {
  customEventTools?: UsableTool[];
  nonHandoffTools: UsableTool[];
  rerouteTools: UsableTool[];
  workflowTools: UsableTool[];
} => {
  const { isLoading, tools } = useGetUsableTools();
  const { view } = useGetBuilderQueryParams();
  const { data } = useGetIntentsQueryWithProduct(
    undefined,
    view === 'email' ? { productOverride: 'interactive_email' } : undefined,
  );
  const { intents: allIntents = [] } = data ?? {};

  const formattedTools: {
    customEventTools?: UsableTool[];
    nonHandoffTools: UsableTool[];
    rerouteTools: UsableTool[];
    workflowTools: UsableTool[];
  } = useMemo(() => {
    if (isLoading) {
      return { nonHandoffTools: [], rerouteTools: [], workflowTools: [] };
    }

    const groups = groupBy('tool_type', [
      ...tools.sort((a, b) => Number(b.isActive) - Number(a.isActive)),
    ]);

    const apiTools = groups['api_call'] ?? [];
    const templateApiTools = groups['template_api_call'] ?? [];
    const systemTools = groups['demo'] ?? [];
    const systemBuiltInTools = groups['system_built_in'] ?? [];
    const handoffTools = (groups['hand_off'] ?? []).sort(sortTools);
    const intentRoutingTools = (groups['intent_routing'] ?? []).sort(sortTools);
    const workflowToolsGroup = (groups['workflow'] ?? []).sort(sortTools);
    const customEventTools = (groups['custom_event'] ?? []).sort(sortTools);
    const mcpTools = groups['mcp'] ?? [];
    const zendeskTools = groups['zendesk_flowstate_action'] ?? [];
    const browserAgentTools = groups['browser_agent'] ?? [];
    const waitTools = groups['wait'] ?? [];

    const nonHandoffTools = [
      ...apiTools,
      ...systemTools,
      ...templateApiTools,
      ...systemBuiltInTools,
      ...mcpTools,
      ...zendeskTools,
      ...browserAgentTools,
      ...waitTools,
    ].sort(sortTools);

    const rerouteTools = [...intentRoutingTools, ...handoffTools]
      .sort(sortTools)
      .map(intent => {
        const fullIntentDetails = allIntents.find(
          ({ intent_definition_id: intentDefinitionId }) =>
            intentDefinitionId === intent.tool_id,
        );

        return {
          ...intent,
          is_autoflow: fullIntentDetails?.is_autoflow || false,
        };
      });

    const workflowTools = [...workflowToolsGroup]
      .sort(sortTools)
      .map(intent => {
        const fullIntentDetails = allIntents.find(
          ({ intent_definition_id: intentDefinitionId }) =>
            intentDefinitionId === intent.tool_id,
        );

        return {
          ...intent,
          is_autoflow: fullIntentDetails?.is_autoflow || false,
        };
      })
      .filter(
        // remove private workflow tools
        tool => !tool.configuration_fields.parent_autoflow_intent_workflow_id,
      );

    return { customEventTools, nonHandoffTools, rerouteTools, workflowTools };
  }, [allIntents, isLoading, tools]);

  return formattedTools;
};

/**
 * Hook to get building block tools for the current autoflow.
 * Returns only workflows where parent_autoflow_intent_workflow_id matches the current workflow ID
 * AND that are present in the autoflow's tool_definitions.
 *
 * @param currentWorkflowId - The current workflow ID
 * @param autopilotWorkflow - Optional autopilot workflow data (for email autopilot mode)
 */
export const useGetBuildingBlockTools = (
  currentWorkflowId?: string,
  autopilotWorkflow?: AutopilotWorkflowModel,
): UsableTool[] => {
  const { isLoading, tools } = useGetUsableTools();

  const buildingBlockTools = useMemo(() => {
    // For email autopilot mode, extract building blocks directly from tool_definitions
    if (autopilotWorkflow) {
      const buildingBlocks =
        autopilotWorkflow.tool_definitions
          ?.filter(
            td =>
              td.tool_type === 'workflow' &&
              td.tool_fields?.parent_autoflow_intent_workflow_id ===
                autopilotWorkflow.autopilot_workflow_id,
          )
          .map(td => ({
            configuration_fields: {
              parent_autoflow_intent_workflow_id:
                td.tool_fields?.parent_autoflow_intent_workflow_id,
            },
            isActive: true,
            tool_id: td.tool_id,
            tool_name: td.name,
            tool_type: td.tool_type,
            toolDefinition: td,
          })) ?? [];

      return buildingBlocks;
    }

    // For regular autoflow mode
    if (isLoading || !currentWorkflowId) {
      return [];
    }

    // Filter to only include building blocks that:
    // 1. Belong to this autoflow (parent_autoflow_intent_workflow_id matches)
    // 2. Are active (present in the current version's tool_definitions)
    // Note: tool.isActive is version-aware via useGetUsableTools/useAutoflowVersionManager
    const buildingBlocks = tools.filter(
      tool =>
        tool.tool_type === 'workflow' &&
        tool.configuration_fields.parent_autoflow_intent_workflow_id ===
          currentWorkflowId &&
        tool.isActive &&
        // Never resurface a removal-only route as an addable building block.
        !tool.isRemovalOnly,
    );

    return buildingBlocks;
  }, [currentWorkflowId, isLoading, autopilotWorkflow, tools]);

  return buildingBlockTools;
};

export const getToolTypeLabel = (
  toolType: AutoflowTool['tool_type'],
  isRecommended: boolean,
): string => {
  if (isRecommended) {
    return 'Recommended';
  }
  switch (toolType) {
    case 'api_call':
      return 'API';
    case 'mcp':
      return 'MCP';
    case 'browser_agent':
      return 'Browser Agent';
    case 'zendesk_flowstate_action':
      return 'Zendesk';
    default:
      return 'Other';
  }
};
